import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Translate } from 'react-localize-redux';
import css from './LoginCard.css';
import { Button, ExpandablePanel, Table, TableCell, TableCellToggle } from '../../../components/widgets';

export const LOGIN_ACCOUNT_CARD_DATA_TEST_ID = 'login-account-card';

export default function LoginCard({
    login,
    partnerIcon,
    partnerDisplayName,
    onReauthorize,
    onToggleAccount,
    getEffectiveEnabled,
    guideLink,
}) {
    const [expanded, setExpanded] = useState(false);

    const { loginId, loginAccounts = [], loginError } = login;

    const accountsById = useMemo(() => {
        const map = new Map();
        loginAccounts.forEach(acc => map.set(acc.accountId, acc));
        return map;
    }, [loginAccounts]);

    const tableMetadata = [
        {
            name: 'enabled',
            displayName: 'STATIC.PAGES.PARTNER_CONFIGURATION.USER_CONFIGURATION_SHELF.ENABLE_COLUMN',
            cellComponent: TableCellToggle,
            cellValues: [
                {
                    propName: 'toggled',
                    dataKey: 'accountId',
                    transformation: accountId => {
                        const row = accountsById.get(accountId);
                        return getEffectiveEnabled(loginId, accountId, row?.enabled);
                    },
                },
                {
                    propName: 'id',
                    dataKey: 'accountId',
                },
            ],
            cellProps: {
                onToggle: (checked, accountId) => {
                    const row = accountsById.get(accountId);
                    if (row) {
                        onToggleAccount(loginId, accountId, checked, row.enabled);
                    }
                },
                tdClassName: css.toggleCell,
                firstLabelKey: null,
                secondLabelKey: null,
            },
            headerAlign: 'left',
        },
        {
            name: 'accountName',
            displayName: 'STATIC.PAGES.PARTNER_CONFIGURATION.USER_CONFIGURATION_SHELF.ACCOUNT_NAME_COLUMN',
            cellComponent: TableCell,
            headerAlign: 'left',
        },
        {
            name: 'accountId',
            displayName: 'STATIC.PAGES.PARTNER_CONFIGURATION.USER_CONFIGURATION_SHELF.ACCOUNT_ID_COLUMN',
            cellComponent: TableCell,
            headerAlign: 'left',
            headerWidth: '200px',
            thStyle: { minWidth: '200px', maxWidth: '200px' },
        },
    ];
    const enabledCount = loginAccounts.filter(a => getEffectiveEnabled(loginId, a.accountId, a.enabled)).length;
    const totalCount = loginAccounts.length;
    const emptyAccountsLogin = totalCount === 0;

    const handleExpandClick = () => {
        setExpanded(prev => !prev);
    };

    const handleReauthorize = e => {
        e.stopPropagation();
        onReauthorize();
    };

    return (
        <ExpandablePanel
            expanded={expanded}
            onExpandClick={handleExpandClick}
            animate
            dataTestId={LOGIN_ACCOUNT_CARD_DATA_TEST_ID}
            overrideClasses={{
                container: css.loginCard,
                headerContainer: css.cardHeader,
                arrowArea: css.arrowArea,
                externalHeaderArea: css.headerContent,
                contentArea: css.accountsTable,
            }}
            header={
                <>
                    <img src={partnerIcon} alt={partnerDisplayName} className={css.partnerIcon} />
                    <div className={css.loginInfo}>
                        <div className={classNames(css.loginId, { [css.error]: loginError })}>{loginId}</div>
                        <div className={loginError ? css.loginError : css.accountCount}>
                            {loginError ? (
                                <Translate id="STATIC.PAGES.PARTNER_CONFIGURATION.USER_CONFIGURATION_SHELF.AUTHENTICATION_ERROR" />
                            ) : emptyAccountsLogin ? (
                                <Translate id="STATIC.PAGES.PARTNER_CONFIGURATION.USER_CONFIGURATION_SHELF.NO_ACCOUNTS_FOR_USER" />
                            ) : (
                                <Translate
                                    id="STATIC.PAGES.PARTNER_CONFIGURATION.USER_CONFIGURATION_SHELF.ACCOUNTS_ENABLED_COUNT"
                                    data={{ enabled: enabledCount, total: totalCount }}
                                />
                            )}
                        </div>
                    </div>
                    <Button type="secondary" className={css.reauthorizeButton} onClick={handleReauthorize}>
                        <Translate id="STATIC.PAGES.PARTNER_CONFIGURATION.USER_CONFIGURATION_SHELF.REAUTHORIZE" />
                    </Button>
                </>
            }
            content={
                loginError ? (
                    <div className={css.errorAccounts}>
                        <Translate
                            id="STATIC.PAGES.PARTNER_CONFIGURATION.USER_CONFIGURATION_SHELF.LOGIN_ERROR_MESSAGE"
                            data={{ partnerName: partnerDisplayName }}
                        />
                    </div>
                ) : emptyAccountsLogin ? (
                    <div className={css.emptyAccounts}>
                        <Translate
                            id="STATIC.PAGES.PARTNER_CONFIGURATION.USER_CONFIGURATION_SHELF.EMPTY_ACCOUNTS_MESSAGE"
                            data={{ partnerName: partnerDisplayName, guideLink: guideLink || '' }}
                        />
                    </div>
                ) : (
                    <Table
                        data={loginAccounts}
                        metadata={tableMetadata}
                        tableClass={css.noBorder}
                        trClass={css.tableRow}
                        theadClass={css.tableHeader}
                        zebraTable={false}
                        rowHoverEnabled={false}
                        showInnerBorder
                    />
                )
            }
        />
    );
}

LoginCard.propTypes = {
    login: PropTypes.shape({
        loginId: PropTypes.string.isRequired,
        loginAccounts: PropTypes.arrayOf(
            PropTypes.shape({
                accountId: PropTypes.string.isRequired,
                accountName: PropTypes.string,
                enabled: PropTypes.bool,
            })
        ),
        loginError: PropTypes.string,
    }).isRequired,
    partnerIcon: PropTypes.string.isRequired,
    partnerDisplayName: PropTypes.string.isRequired,
    onReauthorize: PropTypes.func.isRequired,
    onToggleAccount: PropTypes.func.isRequired,
    getEffectiveEnabled: PropTypes.func.isRequired,
    guideLink: PropTypes.string,
};
